import { XIcon, SlidersHorizontal, Check } from "lucide-react";
import { SearchBar } from "@/components/common/search-bar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { BundleType } from "@/pages/bundles/types/grouped-bundle";

export type FilterProduct = {
  id: string;
  name: string;
  type: string;
  status: string;
  on_dashboard?: boolean | null;
};

const TYPE_OPTIONS: { value: BundleType; label: string }[] = [
  { value: "onboarding", label: "Onboarding" },
  { value: "standard", label: "Standard" },
  { value: "reschedule", label: "Reschedule" },
];

const PRODUCT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "TELE_HEALTH", label: "Tele-health" },
  { value: "ROCKET_LABS", label: "Labs" },
  { value: "ROCKET_PHARMACY", label: "Pharmacy" },
];

const STATUS_OPTIONS = ["ACTIVE", "INACTIVE"];

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}

// A small pill toggle used for the inline chip filters.
function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-8 rounded-full border px-3 text-xs font-medium transition-colors",
        active
          ? "border-[#EA5B34] bg-[#EA5B34]/10 text-[#EA5B34]"
          : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50",
      )}
    >
      {children}
    </button>
  );
}

export type BundleFilterState = {
  types: BundleType[];
  productIds: string[];
  productStatus: string[];
  productTypes: string[];
};

type Props = {
  products: FilterProduct[];
  filters: BundleFilterState;
  onChange: (next: BundleFilterState) => void;
  onOpenVisibility?: () => void;
};

export default function BundleGroupedFilterBar({
  products,
  filters,
  onChange,
  onOpenVisibility,
}: Props) {
  const { types, productIds, productStatus, productTypes } = filters;

  const productFilterCount =
    productIds.length + productStatus.length + productTypes.length;

  const patch = (p: Partial<BundleFilterState>) => onChange({ ...filters, ...p });

  return (
    <div className="flex flex-1 flex-wrap items-center gap-2">
      {/* Bundle-type chips (onboarding / standard / reschedule). */}
      <div className="flex items-center gap-1.5">
        <Chip active={types.length === 0} onClick={() => patch({ types: [] })}>
          All types
        </Chip>
        {TYPE_OPTIONS.map((t) => (
          <Chip
            key={t.value}
            active={types.includes(t.value)}
            onClick={() => patch({ types: toggle(types, t.value) })}
          >
            {t.label}
          </Chip>
        ))}
      </div>

      {/* Product multi-select + status/type chips. */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
            Products
            {productFilterCount > 0 && (
              <Badge className="ml-1.5 h-5 min-w-5 justify-center rounded-full bg-[#EA5B34] px-1 text-[10px] text-white">
                {productFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <p className="text-xs font-semibold text-gray-700">
              Filter products
            </p>
            {productFilterCount > 0 && (
              <button
                type="button"
                className="text-[11px] text-[#EA5B34] hover:underline"
                onClick={() =>
                  patch({
                    productIds: [],
                    productStatus: [],
                    productTypes: [],
                  })
                }
              >
                Clear
              </button>
            )}
          </div>

          <div className="px-3 py-2 border-b space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((s) => (
                <Chip
                  key={s}
                  active={productStatus.includes(s)}
                  onClick={() =>
                    patch({ productStatus: toggle(productStatus, s) })
                  }
                >
                  {s === "ACTIVE" ? "Active" : "Inactive"}
                </Chip>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRODUCT_TYPE_OPTIONS.map((t) => (
                <Chip
                  key={t.value}
                  active={productTypes.includes(t.value)}
                  onClick={() =>
                    patch({ productTypes: toggle(productTypes, t.value) })
                  }
                >
                  {t.label}
                </Chip>
              ))}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            <div className="p-1.5">
              {products.length === 0 ? (
                <p className="px-2 py-3 text-xs text-gray-400">No products.</p>
              ) : (
                products.map((p) => {
                  const checked = productIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        patch({ productIds: toggle(productIds, p.id) })
                      }
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-50"
                    >
                      <Checkbox checked={checked} className="pointer-events-none" />
                      <span className="min-w-0 flex-1 truncate text-xs text-gray-700">
                        {p.name}
                      </span>
                      {p.on_dashboard === true && (
                        <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-600">
                          On dashboard
                        </span>
                      )}
                      {checked && (
                        <Check className="h-3.5 w-3.5 shrink-0 text-[#EA5B34]" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {onOpenVisibility && (
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={onOpenVisibility}
        >
          Visibility
        </Button>
      )}

      {/* Search sits at the right edge of the bar, filters stay left. */}
      <div className="ml-auto flex-1 min-w-[280px] max-w-[420px]">
        <SearchBar
          placeholder="Search name or standard_id…"
          urlParamStrategy="prefix"
          clearButton={{
            enabled: true,
            position: "inside",
            ariaLabel: "Clear search",
            icon: <XIcon className="w-4 h-4" />,
          }}
        />
      </div>
    </div>
  );
}
